/**
 * bitola-script-new.js
 * Calculadora de Bitola de Fios - Versão Modular
 *
 * Calcula a área de seção mínima de fios elétricos para circuitos CC e CA
 */

import { App } from '../src/core/app.js';
import { i18n } from '../src/core/i18n.js';
import { formatarNumero, parsearNumero } from '../src/utils/formatters.js';
import { ExplicacaoResultado } from '../src/components/resultado-explicado.js';

// ============================================
// CONSTANTES
// ============================================

const RESISTIVIDADE_COBRE = 0.0178; // Ω·mm²/m (NBR 5410)
const BITOLAS_COMERCIAIS = [0.25, 0.5, 0.75, 1.0, 1.5, 2.5, 4, 6, 10, 16, 25, 35, 50, 70, 95, 120, 150, 185, 240];
const DISJUNTORES_COMERCIAIS = [6, 10, 13, 16, 20, 25, 32, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 400, 500, 630];
const FATOR_SEGURANCA = 1.25;
const TENSOES_CC_TIPICAS = [3.3, 5, 9, 12, 15, 20, 24, 36, 48, 60, 72, 84, 96];

// ============================================
// CLASSE PRINCIPAL
// ============================================

class BitolaApp extends App {
    constructor() {
        super({
            appName: 'bitola',
            callbacks: {
                aoInicializar: () => this.inicializarBitola(),
                aoTrocarIdioma: () => this.atualizarAposTrocaIdioma()
            }
        });
        this.explicacao = new ExplicacaoResultado('v2-explicacao', i18n);
    }

    /**
     * Getter para acessar traduções do idioma atual
     */
    get traducoes() {
        const idiomaAtual = i18n.obterIdiomaAtual();
        return this.config.traducoes[idiomaAtual] || this.config.traducoes['pt-BR'] || {};
    }

    inicializarBitola() {
        this.configurarEventos();
        this.atualizarResultado();
    }

    atualizarAposTrocaIdioma() {
        this.atualizarResultado();
    }

    configurarEventos() {
        // Info icons (tooltips)
        this.configurarIconesInfo();

        // Inputs de texto
        this.configurarInputsTexto();

        // Sliders
        ['sliderPotencia', 'sliderComprimento', 'sliderTensaoCC', 'sliderQuedaTensao'].forEach(id => {
            const slider = document.getElementById(id);
            if (slider) {
                slider.addEventListener('input', () => this.atualizarResultado());
            }
        });

        // Radio buttons de tipo de corrente
        document.querySelectorAll('input[name="tipoCorrente"]').forEach(radio => {
            radio.addEventListener('change', () => {
                this.atualizarVisibilidadeTensao();
                this.atualizarResultado();
            });
        });

        // Radio buttons de tensão CA
        document.querySelectorAll('input[name="tensaoCA"]').forEach(radio => {
            radio.addEventListener('change', () => this.atualizarResultado());
        });

        // Botões de incremento/decremento (setas + e -)

        // Botão do memorial
        const btnMemorial = document.getElementById('btnMemorial');
        if (btnMemorial) {
            btnMemorial.addEventListener('click', () => this.toggleMemorial());
        }

        // Botão voltar do memorial
        const btnFecharMemorial = document.getElementById('btnFecharMemorial');
        if (btnFecharMemorial) {
            btnFecharMemorial.addEventListener('click', () => this.toggleMemorial());
        }

        document.querySelectorAll('.btn-voltar-memorial').forEach(btn => {
            btn.addEventListener('click', () => this.toggleMemorial());
        });

        // Inicializar visibilidade
        this.atualizarVisibilidadeTensao();
    }

    configurarIconesInfo() {
        const infoIcons = [
            { iconId: 'infoIconPotencia', descricaoId: 'descricaoPotencia' },
            { iconId: 'infoIconComprimento', descricaoId: 'descricaoComprimento' },
            { iconId: 'infoIconTensaoCC', descricaoId: 'descricaoTensaoCC' },
            { iconId: 'infoIconQuedaTensao', descricaoId: 'descricaoQuedaTensao' }
        ];

        infoIcons.forEach(({ iconId, descricaoId }) => {
            const icon = document.getElementById(iconId);
            const descricao = document.getElementById(descricaoId);

            if (icon && descricao) {
                // Garante estado inicial
                descricao.style.display = 'none';

                icon.addEventListener('click', () => {
                    const isVisible = descricao.style.display === 'block';
                    descricao.style.display = isVisible ? 'none' : 'block';
                });

                // Também permite usar Enter quando focado
                icon.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        const isVisible = descricao.style.display === 'block';
                        descricao.style.display = isVisible ? 'none' : 'block';
                    }
                });
            } else {
            }
        });
    }

    configurarInputsTexto() {
        const inputPotencia = document.getElementById('inputPotencia');
        const inputComprimento = document.getElementById('inputComprimento');
        const inputTensaoCC = document.getElementById('inputTensaoCC');
        const inputQuedaTensao = document.getElementById('inputQuedaTensao');

        const sliderPotencia = document.getElementById('sliderPotencia');
        const sliderComprimento = document.getElementById('sliderComprimento');
        const sliderTensaoCC = document.getElementById('sliderTensaoCC');
        const sliderQuedaTensao = document.getElementById('sliderQuedaTensao');

        const atualizarDoInput = (input, slider, tipo) => {
            const numero = this.parsearValor(input.value);

            if (!isNaN(numero)) {
                const min = parseFloat(slider.min);
                const max = parseFloat(slider.max);
                let valorFinal = numero;

                if (tipo === 'potencia') {
                    valorFinal = Math.min(999999, Math.max(min, numero));
                    input.dataset.valorReal = valorFinal;
                    slider.value = Math.min(max, valorFinal);
                } else if (tipo === 'tensaoCC') {
                    valorFinal = Math.max(min, numero);
                    input.dataset.valorReal = valorFinal;
                    // Ajusta slider para a tensao tipica mais proxima
                    let indiceMaisProximo = 0;
                    let menorDiferenca = Infinity;
                    for (let i = 0; i < TENSOES_CC_TIPICAS.length; i++) {
                        const diferenca = Math.abs(TENSOES_CC_TIPICAS[i] - valorFinal);
                        if (diferenca < menorDiferenca) {
                            menorDiferenca = diferenca;
                            indiceMaisProximo = i;
                        }
                    }
                    slider.value = indiceMaisProximo;
                } else {
                    valorFinal = Math.max(min, Math.min(max, numero));
                    slider.value = valorFinal;
                }

                this.atualizarResultado();
            }
        };

        if (inputPotencia && sliderPotencia) {
            inputPotencia.addEventListener('blur', () => atualizarDoInput(inputPotencia, sliderPotencia, 'potencia'));
            inputPotencia.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    atualizarDoInput(inputPotencia, sliderPotencia, 'potencia');
                    inputPotencia.blur();
                }
            });
            sliderPotencia.addEventListener('input', () => {
                inputPotencia.dataset.valorReal = sliderPotencia.value;
            });
        }

        if (inputComprimento && sliderComprimento) {
            inputComprimento.addEventListener('blur', () => atualizarDoInput(inputComprimento, sliderComprimento, 'comprimento'));
            inputComprimento.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    atualizarDoInput(inputComprimento, sliderComprimento, 'comprimento');
                    inputComprimento.blur();
                }
            });
        }

        if (inputTensaoCC && sliderTensaoCC) {
            inputTensaoCC.addEventListener('blur', () => atualizarDoInput(inputTensaoCC, sliderTensaoCC, 'tensaoCC'));
            inputTensaoCC.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    atualizarDoInput(inputTensaoCC, sliderTensaoCC, 'tensaoCC');
                    inputTensaoCC.blur();
                }
            });
            sliderTensaoCC.addEventListener('input', () => {
                const indice = parseInt(sliderTensaoCC.value || 0);
                const tensao = TENSOES_CC_TIPICAS[indice] || 12;
                inputTensaoCC.dataset.valorReal = tensao;
            });
        }

        if (inputQuedaTensao && sliderQuedaTensao) {
            inputQuedaTensao.addEventListener('blur', () => atualizarDoInput(inputQuedaTensao, sliderQuedaTensao, 'queda'));
            inputQuedaTensao.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    atualizarDoInput(inputQuedaTensao, sliderQuedaTensao, 'queda');
                    inputQuedaTensao.blur();
                }
            });
        }
    }

    atualizarVisibilidadeTensao() {
        const tipoCorrente = document.querySelector('input[name="tipoCorrente"]:checked')?.value;
        const secaoTensaoCC = document.getElementById('secaoTensaoCC');
        const tensaoCAInline = document.getElementById('tensaoCAInline');

        if (tipoCorrente === 'ca') {
            if (secaoTensaoCC) secaoTensaoCC.style.display = 'none';
            if (tensaoCAInline) tensaoCAInline.style.display = 'flex';
        } else {
            if (secaoTensaoCC) secaoTensaoCC.style.display = 'block';
            if (tensaoCAInline) tensaoCAInline.style.display = 'none';
        }
    }

    parsearValor(texto) {
        if (!texto || !/[0-9]/.test(texto.toString())) return NaN;
        const valor = texto.toString().trim();
        return parsearNumero(valor);
    }

    obterTensaoAtual() {
        const tipoCorrente = document.querySelector('input[name="tipoCorrente"]:checked')?.value;

        if (tipoCorrente === 'ca') {
            const tensaoCA = document.querySelector('input[name="tensaoCA"]:checked')?.value;
            return parseFloat(tensaoCA) || 110;
        } else {
            const sliderTensaoCC = document.getElementById('sliderTensaoCC');
            const indice = parseInt(sliderTensaoCC?.value || 0);
            return TENSOES_CC_TIPICAS[indice] || 12;
        }
    }

    calcularCorrente(potencia, tensao) {
        if (tensao === 0) return 0;
        return potencia / tensao;
    }

    calcularAreaMinima(comprimento, corrente, tensao, quedaPercentual) {
        const quedaVolts = (quedaPercentual / 100) * tensao;
        if (quedaVolts === 0) return Infinity;
        return (2 * RESISTIVIDADE_COBRE * comprimento * corrente) / quedaVolts;
    }

    selecionarBitolaComercial(areaMinima) {
        if (!isFinite(areaMinima) || areaMinima <= 0) {
            return { bitola: BITOLAS_COMERCIAIS[0], multiplicador: 1, bitolaEquivalente: BITOLAS_COMERCIAIS[0] };
        }

        const areaComSeguranca = areaMinima * FATOR_SEGURANCA;

        for (let i = 0; i < BITOLAS_COMERCIAIS.length; i++) {
            if (BITOLAS_COMERCIAIS[i] >= areaComSeguranca) {
                return { bitola: BITOLAS_COMERCIAIS[i], multiplicador: 1, bitolaEquivalente: BITOLAS_COMERCIAIS[i] };
            }
        }

        const bitolaMaxima = BITOLAS_COMERCIAIS[BITOLAS_COMERCIAIS.length - 1];
        const multiplicador = Math.ceil(areaComSeguranca / bitolaMaxima);
        return {
            bitola: bitolaMaxima,
            multiplicador,
            bitolaEquivalente: bitolaMaxima * multiplicador
        };
    }

    formatarBitolaComercial(bitola, multiplicador) {
        const textoBitola = `${formatarNumero(bitola, 2)} mm²`;
        if (multiplicador > 1) {
            return `${textoBitola} (${multiplicador}x)`;
        }
        return textoBitola;
    }

    selecionarDisjuntorComercial(corrente) {
        const correnteComSeguranca = corrente * FATOR_SEGURANCA;

        for (let i = 0; i < DISJUNTORES_COMERCIAIS.length; i++) {
            if (DISJUNTORES_COMERCIAIS[i] >= correnteComSeguranca) {
                return DISJUNTORES_COMERCIAIS[i];
            }
        }

        return DISJUNTORES_COMERCIAIS[DISJUNTORES_COMERCIAIS.length - 1];
    }

    calcularQuedaReal(comprimento, corrente, tensao, bitola) {
        if (tensao === 0 || bitola === 0) return 0;
        const quedaVolts = (2 * RESISTIVIDADE_COBRE * comprimento * corrente) / bitola;
        return (quedaVolts / tensao) * 100;
    }

    formatarComSufixo(valor) {
        return formatarNumero(valor, 0);
    }

    /**
     * Monta a nota que explica a margem de segurança da NBR (+25%),
     * mostrando por que a bitola comercial pode "pular" o tamanho logo acima
     * da área mínima (ex.: 1,22 × 1,25 = 1,53 → 2,50 mm²).
     */
    montarNotaMargem(areaMin, bitolaSelecionada) {
        if (!isFinite(areaMin) || areaMin <= 0 || !bitolaSelecionada || !isFinite(bitolaSelecionada.bitola)) {
            return '';
        }

        const areaSeg = areaMin * FATOR_SEGURANCA;
        const areaMinStr = formatarNumero(areaMin, 2);
        const areaSegStr = formatarNumero(areaSeg, 2);
        const bitolaStr = this.formatarBitolaComercial(bitolaSelecionada.bitola, bitolaSelecionada.multiplicador);

        // A norma citada acompanha a jurisdição do idioma: SS 436 40 00 é a
        // aplicação sueca da IEC 60364.
        return i18n.porIdioma({
            'pt-BR': `<strong>Margem de segurança NBR 5410 (+25%):</strong> ${areaMinStr} × 1,25 = ${areaSegStr} mm² → menor bitola comercial que atende: <strong>${bitolaStr}</strong>.`,
            'it-IT': `<strong>Margine di sicurezza NBR 5410 (+25%):</strong> ${areaMinStr} × 1,25 = ${areaSegStr} mm² → sezione commerciale minima adeguata: <strong>${bitolaStr}</strong>.`,
            'sv-SE': `<strong>Säkerhetsmarginal enligt SS 436 40 00 (+25 %):</strong> ${areaMinStr} × 1,25 = ${areaSegStr} mm² → minsta standardarea som klarar kravet: <strong>${bitolaStr}</strong>.`
        });
    }

    atualizarResultado() {
        // Obter valores dos inputs
        const inputPotencia = document.getElementById('inputPotencia');
        const inputComprimento = document.getElementById('inputComprimento');
        const inputQuedaTensao = document.getElementById('inputQuedaTensao');
        const inputTensaoCC = document.getElementById('inputTensaoCC');

        const sliderPotencia = document.getElementById('sliderPotencia');
        const sliderComprimento = document.getElementById('sliderComprimento');
        const sliderQuedaTensao = document.getElementById('sliderQuedaTensao');
        const sliderTensaoCC = document.getElementById('sliderTensaoCC');

        const potenciaValorReal = inputPotencia?.dataset.valorReal;
        const potencia = potenciaValorReal ? parseFloat(potenciaValorReal) : parseFloat(sliderPotencia?.value || 100);
        const comprimento = parseFloat(sliderComprimento?.value || 10);
        const quedaPercentual = parseFloat(sliderQuedaTensao?.value || 3);
        const tensao = this.obterTensaoAtual();

        // Atualizar inputs de texto
        if (inputPotencia) {
            inputPotencia.value = this.formatarComSufixo(potencia);
            inputPotencia.dataset.valorReal = potencia;
        }
        if (inputComprimento) inputComprimento.value = formatarNumero(comprimento, 1);
        if (inputQuedaTensao) inputQuedaTensao.value = formatarNumero(quedaPercentual, 1);
        if (inputTensaoCC) {
            const tensaoValorReal = inputTensaoCC.dataset.valorReal;
            if (tensaoValorReal) {
                inputTensaoCC.value = formatarNumero(parseFloat(tensaoValorReal), 1);
            } else {
                const indice = parseInt(sliderTensaoCC?.value || 0);
                inputTensaoCC.value = formatarNumero(TENSOES_CC_TIPICAS[indice] || 12, 1);
            }
        }

        // Validação básica
        if (potencia <= 0 || comprimento <= 0 || tensao <= 0 || quedaPercentual <= 0) {
            this.limparResultados();
            return;
        }

        // Calcular
        const corrente = this.calcularCorrente(potencia, tensao);
        const areaMin = this.calcularAreaMinima(comprimento, corrente, tensao, quedaPercentual);
        const bitolaSelecionada = this.selecionarBitolaComercial(areaMin);
        const quedaRealPercentual = this.calcularQuedaReal(comprimento, corrente, tensao, bitolaSelecionada.bitolaEquivalente);
        const disjuntor = this.selecionarDisjuntorComercial(corrente);

        // Atualizar interface
        const areaMinima = document.getElementById('areaMinima');
        const bitolaComercial = document.getElementById('bitolaComercial');
        const correnteCircuito = document.getElementById('correnteCircuito');
        const quedaReal = document.getElementById('quedaReal');
        const disjuntorComercial = document.getElementById('disjuntorComercial');

        if (areaMinima) {
            areaMinima.textContent = isFinite(areaMin) ? formatarNumero(areaMin, 2) + ' mm²' : '-';
        }
        if (bitolaComercial) {
            bitolaComercial.textContent = isFinite(bitolaSelecionada.bitola)
                ? this.formatarBitolaComercial(bitolaSelecionada.bitola, bitolaSelecionada.multiplicador)
                : '-';
        }
        if (correnteCircuito) {
            correnteCircuito.textContent = isFinite(corrente) ? formatarNumero(corrente, 2) + ' A' : '-';
        }
        if (quedaReal) {
            quedaReal.textContent = isFinite(quedaRealPercentual) ? formatarNumero(quedaRealPercentual, 2) + '%' : '-';
        }
        if (disjuntorComercial) {
            disjuntorComercial.textContent = isFinite(disjuntor) ? formatarNumero(disjuntor, 0) + ' A' : '-';
        }

        const bitolaMargemNota = document.getElementById('bitolaMargemNota');
        if (bitolaMargemNota) {
            bitolaMargemNota.innerHTML = this.montarNotaMargem(areaMin, bitolaSelecionada);
        }

        // Atualizar memorial se visível
        const memorial = document.getElementById('memorialSection');
        if (memorial && memorial.style.display !== 'none') {
            this.atualizarMemorial(
                potencia,
                comprimento,
                tensao,
                quedaPercentual,
                corrente,
                areaMin,
                bitolaSelecionada,
                quedaRealPercentual,
                disjuntor
            );
        }

        // Painel de explicação V2.0
        this.renderizarExplicacao({ potencia, comprimento, tensao, corrente, areaMin, bitolaSelecionada, quedaRealPercentual, quedaPercentual, disjuntor });
    }

    limparResultados() {
        const ids = ['areaMinima', 'bitolaComercial', 'correnteCircuito', 'quedaReal', 'disjuntorComercial'];
        ids.forEach(id => {
            const el = document.getElementById(id);
            if (el) el.textContent = '-';
        });
        const bitolaMargemNota = document.getElementById('bitolaMargemNota');
        if (bitolaMargemNota) bitolaMargemNota.innerHTML = '';
        this.explicacao.limpar();
    }

    renderizarExplicacao({ potencia, comprimento, tensao, corrente, areaMin, bitolaSelecionada, quedaRealPercentual, quedaPercentual, disjuntor }) {
        const t = mapa => i18n.porIdioma(mapa);
        const bitolaStr = isFinite(bitolaSelecionada?.bitola)
            ? this.formatarBitolaComercial(bitolaSelecionada.bitola, bitolaSelecionada.multiplicador)
            : '-';

        const quedaOk = quedaRealPercentual <= quedaPercentual;
        const dica = quedaOk
            ? t({
                'pt-BR': 'A queda de tensão está dentro do limite. O fio dimensionado é adequado para o circuito.',
                'it-IT': 'La caduta di tensione è nei limiti. Il cavo dimensionato è adeguato per il circuito.',
                'sv-SE': 'Spänningsfallet ligger inom gränsen. Den valda arean räcker för kretsen.'
            })
            : t({
                'pt-BR': 'A queda de tensão real excede o limite especificado. Considere aumentar a bitola ou reduzir o comprimento.',
                'it-IT': 'La caduta di tensione reale supera il limite. Considera di aumentare la sezione o ridurre la lunghezza.',
                'sv-SE': 'Det verkliga spänningsfallet överskrider gränsen. Öka arean eller korta ned ledningen.'
            });

        this.explicacao.renderizar({
            destaque: t({
                'pt-BR': `Use fio de ${bitolaStr} para proteger este circuito com segurança.`,
                'it-IT': `Usa cavo da ${bitolaStr} per proteggere questo circuito in sicurezza.`,
                'sv-SE': `Använd ${bitolaStr} för att skydda den här kretsen på ett säkert sätt.`
            }),
            linhas: [
                {
                    icone: '⚡',
                    titulo: t({ 'pt-BR': 'Corrente do Circuito', 'it-IT': 'Corrente del Circuito', 'sv-SE': 'Kretsens ström' }),
                    valor: `${formatarNumero(corrente, 2)} A`,
                    descricao: t({
                        'pt-BR': `Com ${formatarNumero(potencia, 0)}W em ${formatarNumero(tensao, 1)}V. Quanto maior a corrente, mais grosso precisa ser o fio.`,
                        'it-IT': `Con ${formatarNumero(potencia, 0)}W a ${formatarNumero(tensao, 1)}V. Maggiore la corrente, più grosso deve essere il cavo.`,
                        'sv-SE': `Med ${formatarNumero(potencia, 0)} W vid ${formatarNumero(tensao, 1)} V. Ju högre ström, desto grövre ledare krävs.`
                    })
                },
                {
                    icone: '📏',
                    titulo: t({ 'pt-BR': 'Área Mínima Calculada', 'it-IT': 'Sezione Minima Calcolata', 'sv-SE': 'Beräknad minsta area' }),
                    valor: `${formatarNumero(areaMin, 2)} mm²`,
                    descricao: t({
                        'pt-BR': 'Calculada pela fórmula da resistividade do cobre (NBR 5410). O comercial aplica +25% de margem de segurança.',
                        'it-IT': 'Calcolata con la formula della resistività del rame. Il cavo commerciale applica un margine di sicurezza del +25%.',
                        'sv-SE': 'Beräknad med formeln för kopparns resistivitet (SS 436 40 00). Standardarean får +25 % säkerhetsmarginal.'
                    })
                },
                {
                    icone: '🔌',
                    titulo: t({ 'pt-BR': 'Bitola Comercial', 'it-IT': 'Sezione Commerciale', 'sv-SE': 'Standardarea' }),
                    valor: bitolaStr,
                    descricao: t({
                        'pt-BR': 'É a bitola padrão mais próxima disponível nas lojas. Esta é a que você vai comprar.',
                        'it-IT': 'È la sezione standard più vicina disponibile nei negozi. Questo è ciò che acquisterai.',
                        'sv-SE': 'Det är närmaste standardarea som finns i handeln. Det är den du köper.'
                    })
                },
                {
                    icone: '📉',
                    titulo: t({ 'pt-BR': 'Queda de Tensão Real', 'it-IT': 'Caduta di Tensione Reale', 'sv-SE': 'Verkligt spänningsfall' }),
                    valor: `${formatarNumero(quedaRealPercentual, 2)}% ${quedaOk ? '✅' : '⚠️'}`,
                    descricao: t({
                        'pt-BR': `O cálculo usa o limite definido de ${formatarNumero(quedaPercentual, 1)}% para conferir a queda real.`,
                        'it-IT': `Il calcolo usa il limite impostato di ${formatarNumero(quedaPercentual, 1)}% per verificare la caduta reale.`,
                        'sv-SE': `Beräkningen jämför det verkliga fallet mot den satta gränsen på ${formatarNumero(quedaPercentual, 1)} %.`
                    })
                },
                {
                    icone: '🔬',
                    titulo: t({ 'pt-BR': 'Disjuntor Recomendado', 'it-IT': 'Interruttore Raccomandato', 'sv-SE': 'Rekommenderad dvärgbrytare' }),
                    valor: `${formatarNumero(disjuntor, 0)} A`,
                    descricao: t({
                        'pt-BR': 'Desliga automaticamente se a corrente ultrapassar o limite, protegendo o fio e os equipamentos.',
                        'it-IT': 'Si disattiva automaticamente se la corrente supera il limite, proteggendo il cavo e le apparecchiature.',
                        'sv-SE': 'Löser ut automatiskt om strömmen överskrider gränsen och skyddar både ledning och utrustning.'
                    })
                }
            ],
            dica,
            norma: t({
                'pt-BR': 'ABNT NBR 5410:2004+Errata 1:2008 — Instalações Elétricas de Baixa Tensão',
                'it-IT': 'ABNT NBR 5410:2004 — Impianti Elettrici a Bassa Tensione',
                'sv-SE': 'SS 436 40 00 — Elinstallationsreglerna (svensk tillämpning av IEC 60364)'
            })
        });
    }

    atualizarMemorial(potencia, comprimento, tensao, quedaPercentual, corrente, areaMin, bitolaSelecionada, quedaReal, disjuntor) {
        // Atualizar exemplos - apenas valores numéricos e unidades
        const exCorrente = document.getElementById('memorial-exemplo-corrente');
        const exArea = document.getElementById('memorial-exemplo-area');
        const exBitola = document.getElementById('memorial-exemplo-bitola');
        const exQueda = document.getElementById('memorial-exemplo-queda');
        const exDisjuntor = document.getElementById('memorial-exemplo-disjuntor');

        if (exCorrente) {
            exCorrente.textContent = `${formatarNumero(potencia, 0)}${this.traducoes.unidades.watt} ÷ ${formatarNumero(tensao, 1)}${this.traducoes.unidades.volt} = ${formatarNumero(corrente, 2)}${this.traducoes.unidades.ampere}`;
        }
        if (exArea) {
            const quedaVolts = (quedaPercentual / 100) * tensao;
            exArea.textContent = `(2 × 0.0178 × ${formatarNumero(comprimento, 1)} × ${formatarNumero(corrente, 2)}) ÷ ${formatarNumero(quedaVolts, 2)} = ${formatarNumero(areaMin, 2)} ${this.traducoes.unidades.mm2}`;
        }
        if (exBitola) {
            exBitola.textContent = `${formatarNumero(areaMin, 2)} ${this.traducoes.unidades.mm2} × 1.25 = ${formatarNumero(areaMin * 1.25, 2)} ${this.traducoes.unidades.mm2} → ${this.formatarBitolaComercial(bitolaSelecionada.bitola, bitolaSelecionada.multiplicador)}`;
        }
        if (exQueda) {
            exQueda.textContent = `((2 × 0.0178 × ${formatarNumero(comprimento, 1)} × ${formatarNumero(corrente, 2)}) ÷ ${formatarNumero(bitolaSelecionada.bitolaEquivalente, 2)}) ÷ ${formatarNumero(tensao, 1)} × 100 = ${formatarNumero(quedaReal, 2)}${this.traducoes.unidades.percent}`;
        }
        if (exDisjuntor) {
            exDisjuntor.textContent = `${formatarNumero(corrente, 2)}${this.traducoes.unidades.ampere} × 1.25 = ${formatarNumero(corrente * 1.25, 2)}${this.traducoes.unidades.ampere} → ${formatarNumero(disjuntor, 0)}${this.traducoes.unidades.ampere}`;
        }

        // Atualizar resumo
        const resumoCorrente = document.getElementById('resumo-corrente');
        const resumoArea = document.getElementById('resumo-area');
        const resumoBitola = document.getElementById('resumo-bitola');
        const resumoQueda = document.getElementById('resumo-queda');
        const resumoDisjuntor = document.getElementById('resumo-disjuntor');

        if (resumoCorrente) resumoCorrente.textContent = formatarNumero(corrente, 2) + ' ' + this.traducoes.unidades.ampere;
        if (resumoArea) resumoArea.textContent = formatarNumero(areaMin, 2) + ' ' + this.traducoes.unidades.mm2;
        if (resumoBitola) resumoBitola.textContent = this.formatarBitolaComercial(bitolaSelecionada.bitola, bitolaSelecionada.multiplicador);
        if (resumoQueda) resumoQueda.textContent = formatarNumero(quedaReal, 2) + this.traducoes.unidades.percent;
        if (resumoDisjuntor) resumoDisjuntor.textContent = formatarNumero(disjuntor, 0) + ' ' + this.traducoes.unidades.ampere;
    }

    toggleMemorial() {
        const memorial = document.getElementById('memorialSection');
        const resultados = document.getElementById('resultadosSection');

        if (!memorial) return;

        if (memorial.style.display === 'none' || memorial.style.display === '') {
            this.atualizarResultado(); // Atualiza memorial com valores atuais
            memorial.style.display = 'block';
            if (resultados) resultados.style.display = 'none';
        } else {
            memorial.style.display = 'none';
            if (resultados) resultados.style.display = 'block';
        }
    }
}

// ============================================
// INICIALIZAÇÃO
// ============================================

// Garante que o DOM está completamente carregado
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        const app = new BitolaApp();
        app.inicializar();
    });
} else {
    const app = new BitolaApp();
    app.inicializar();
}
